import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Header from '@/components/landing/layout/Header';
import Footer from '@/components/landing/layout/Footer';
import TrustedBy from '@/components/landing/common/TrustedBy';
import CustomCalendar from '@/components/landing/common/CustomCalendar';
import CustomDropdown from '@/components/landing/common/CustomDropdown';
import { ArrowLeft, Send, Clock } from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import calendlyService from '@/service/calendlyService';

const industries = [
    'Apparel and Footwear',
    'Arts and Crafts',
    'Baby',
    'Bags and Luggage',
    'Beauty and Cosmetics',
    'Big Box Retailers',
    'Camera and Video',
    'Consumables',
    'Consumer Electronics',
    'Furniture',
    'Glasses and Eyewear',
    'Home Decor, Bedding, and Cookware',
    'Home Fitness',
    'Home Improvement',
    'Jewelry',
    'Musical Instruments and Audio Equipment',
    'Sports and Outdoors',
    'Vehicle Parts and Accessories',
];

const annualSalesRanges = [
    'Less than ₹10L',
    '₹10L - ₹50L',
    '₹50L - ₹1Cr',
    '₹1Cr - ₹5Cr',
    '₹5Cr - ₹10Cr',
    'More than ₹10Cr',
];

interface FormData {
    name: string;
    email: string;
    phone: string;
    company: string;
    industry: string;
    annualSales: string;
    website: string;
    hearAbout: string;
}

const BookMeeting: React.FC = () => {
    const [step, setStep] = useState<'calendar' | 'form' | 'success'>('calendar');
    const [bookingPending, setBookingPending] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedTime, setSelectedTime] = useState<string | null>(null);
    const [availableSlots, setAvailableSlots] = useState<string[]>([]);
    const [isLoadingSlots, setIsLoadingSlots] = useState(false);
    const lastFetchedDate = useRef<string | null>(null);
    const lastFetchedMonth = useRef<string | null>(null);

    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    
    const [formData, setFormData] = useState<FormData>({
        name: queryParams.get('name') || '',
        email: queryParams.get('email') || '',
        phone: '',
        company: '',
        industry: '',
        annualSales: '',
        website: '',
        hearAbout: '',
    });

    const [highlightedDates, setHighlightedDates] = useState<string[]>([]);

    const fetchMonthlyAvailability = async (date: Date) => {
        const monthKey = format(date, 'yyyy-MM');
        if (lastFetchedMonth.current === monthKey && !isLoadingSlots) return;
        
        lastFetchedMonth.current = monthKey;
        setIsLoadingSlots(true);
        try {
            const startStr = format(startOfMonth(date), "yyyy-MM-dd'T'HH:mm:ss'Z'");
            const endStr = format(endOfMonth(date), "yyyy-MM-dd'T'HH:mm:ss'Z'");
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            
            const response = await calendlyService.getAvailabilityRange(startStr, endStr, timezone);
            
            // Backend now provides unique date strings directly
            setHighlightedDates(response.available_dates);
        } catch (error) {
            console.error("Error fetching monthly availability:", error);
        } finally {
            setIsLoadingSlots(false);
        }
    };

    useEffect(() => {
        const name = queryParams.get('name');
        if (name) {
            setFormData(prev => ({
                ...prev,
                name: name
            }));
        }
    }, [location.search]);

    const handleDateChange = async (date: Date) => {
        const dateString = format(date, 'yyyy-MM-dd');
        
        // Prevent duplicate calls for the same date
        if (lastFetchedDate.current === dateString && !isLoadingSlots) return;
        
        lastFetchedDate.current = dateString;
        setIsLoadingSlots(true);
        setSelectedDate(date);
        
        try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const response = await calendlyService.getAvailability(dateString, timezone);
            setAvailableSlots(response.available_slots);
        } catch (error) {
            toast.error("Could not fetch available slots. Please try again.");
            setAvailableSlots([]);
        } finally {
            setIsLoadingSlots(false);
        }
    };

    useEffect(() => {
        // Initial load for current month and availability for today
        const today = new Date();
        fetchMonthlyAvailability(today);
        handleDateChange(today);
    }, []);

    const handleMonthChange = (date: Date) => {
        fetchMonthlyAvailability(date);
    };

    const handleSelectTime = (time: string) => {
        setSelectedTime(time);
        
        // If we already have the details, bypass the form and auto-book
        if (formData.name && formData.email) {
            submitBooking(time);
        } else {
            setStep('form');
        }
    };

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const submitBooking = async (time: string) => {
        const toastId = toast.loading('Scheduling your meeting...');
        try {
            await calendlyService.createBooking({
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                company: formData.company,
                industry: formData.industry,
                annualSales: formData.annualSales,
                website: formData.website,
                hearAbout: formData.hearAbout,
                start_time: time,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            });
            toast.success('Meeting scheduled successfully!', { id: toastId });
            setBookingPending(false);
            setStep('success');
            if (window.gtag) {
                window.gtag('event', 'demo_form_success');
            }
        } catch (err: any) {
            console.error("Booking failed:", err);
            if (err?.leadSaved) {
                toast.success("Request received! Our team will confirm your slot shortly.", { id: toastId });
                setBookingPending(true);
                setStep('success');
                if (window.gtag) {
                    window.gtag('event', 'demo_form_success');
                }
                return;
            }
            const reason = typeof err === 'string' ? err : err?.message;
            toast.error(`Failed to schedule: ${reason || 'Please try again.'}`, { id: toastId });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        submitBooking(selectedTime!);
    };

    return (
        <div className="tw-min-h-screen tw-bg-black tw-text-white tw-overflow-x-hidden">
            <Header />

            {/* Spacer for Fixed Header */}
            <div className="tw-h-20 lg:tw-h-24" />

            <main className="tw-relative tw-pb-24">
                {/* Background Pattern / Gradients */}
                <div className="tw-absolute tw-inset-0 tw-bg-grid-white/[0.05] tw-bg-[length:32px_32px]" />
                <div className="tw-absolute tw-top-[-10%] tw-left-1/2 -tw-translate-x-1/2 tw-w-full tw-h-[800px] tw-bg-gradient-to-b tw-from-[#00C5FB]/10 tw-to-transparent tw-blur-[120px] -tw-z-10" />

                <div className="tw-relative tw-max-w-6xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-py-12 md:tw-py-20 tw-text-center">
                    {/* Hero Section */}
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="tw-mb-16"
                    >
                        <h1 className="tw-text-4xl md:tw-text-6xl tw-font-bold tw-text-white tw-mb-6 tw-tracking-tight">
                            Your Personal <span className="tw-text-[#00C5FB]">Expert Walkthrough</span>
                        </h1>
                        <p className="tw-text-gray-400 tw-text-lg md:tw-text-xl tw-max-w-2xl tw-mx-auto">
                            Experience the future of post-purchase operations. Book a session with our experts to see how we can transform your customer journey.
                        </p>
                    </motion.div>

                    {/* Step-based Content Container */}
                    <div className="tw-relative tw-z-10 tw-min-h-[600px] tw-bg-white/[0.03] tw-backdrop-blur-md tw-border tw-border-white/10 tw-rounded-[2.5rem] tw-overflow-visible tw-shadow-[0_0_50px_-12px_rgba(0,197,251,0.3)]">
                        <AnimatePresence mode="wait">
                            {step === 'calendar' && (
                                <motion.div
                                    key="calendar-step"
                                    initial={{ opacity: 0, scale: 0.98 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, x: -100 }}
                                    transition={{ duration: 0.4 }}
                                >
                                    <CustomCalendar 
                                        availableSlots={availableSlots}
                                        highlightedDates={highlightedDates}
                                        isLoadingSlots={isLoadingSlots}
                                        onDateChange={handleDateChange}
                                        onSelectTime={handleSelectTime}
                                        onMonthChange={handleMonthChange}
                                    />
                                </motion.div>
                            )}

                            {step === 'form' && (
                                <motion.div
                                    key="form-step"
                                    initial={{ opacity: 0, x: 100 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    className="tw-max-w-3xl tw-mx-auto tw-p-8 md:tw-p-12 tw-text-left"
                                >
                                    <button 
                                        onClick={() => setStep('calendar')}
                                        className="tw-flex tw-items-center tw-gap-2 tw-text-[#00C5FB] tw-mb-8 hover:tw-opacity-80"
                                    >
                                        <ArrowLeft className="tw-w-4 tw-h-4" />
                                        Back to Calendar
                                    </button>

                                    <div className="tw-mb-8">
                                        <h2 className="tw-text-3xl tw-font-bold tw-text-white tw-mb-2">Confirm Your Details</h2>
                                        <p className="tw-text-gray-400">Please provide a few details to help our experts prepare for your demo.</p>
                                    </div>

                                    <form onSubmit={handleSubmit} className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                                        <div className="md:tw-col-span-2">
                                            <label className="tw-block tw-text-xs tw-font-bold tw-uppercase tw-text-gray-500 tw-mb-2">Full Name</label>
                                            <input 
                                                required
                                                name="name"
                                                value={formData.name}
                                                onChange={handleFormChange}
                                                className="tw-w-full tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-xl tw-px-4 tw-py-3 focus:tw-border-[#00C5FB] tw-outline-none tw-transition-all"
                                                placeholder="John Doe"
                                            />
                                        </div>
                                        <div>
                                            <label className="tw-block tw-text-xs tw-font-bold tw-uppercase tw-text-gray-500 tw-mb-2">Work Email</label>
                                            <input 
                                                required
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleFormChange}
                                                className="tw-w-full tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-xl tw-px-4 tw-py-3 focus:tw-border-[#00C5FB] tw-outline-none tw-transition-all"
                                                placeholder="john@company.com"
                                            />
                                        </div>
                                        <div>
                                            <label className="tw-block tw-text-xs tw-font-bold tw-uppercase tw-text-gray-500 tw-mb-2">Phone Number</label>
                                            <input 
                                                required
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleFormChange}
                                                className="tw-w-full tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-xl tw-px-4 tw-py-3 focus:tw-border-[#00C5FB] tw-outline-none tw-transition-all"
                                                placeholder="+91 98765 43210"
                                            />
                                        </div>
                                        <div>
                                            <label className="tw-block tw-text-xs tw-font-bold tw-uppercase tw-text-gray-500 tw-mb-2">Company Name</label>
                                            <input 
                                                required
                                                name="company"
                                                value={formData.company}
                                                onChange={handleFormChange}
                                                className="tw-w-full tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-xl tw-px-4 tw-py-3 focus:tw-border-[#00C5FB] tw-outline-none tw-transition-all"
                                                placeholder="Protega Inc."
                                            />
                                        </div>
                                        <div className="tw-relative tw-z-[30]">
                                            <CustomDropdown 
                                                label="Industry"
                                                options={industries}
                                                value={formData.industry}
                                                onChange={(val) => setFormData(prev => ({ ...prev, industry: val }))}
                                                placeholder="Select industry"
                                            />
                                        </div>
                                        <div className="tw-relative tw-z-[20]">
                                            <CustomDropdown 
                                                label="Annual Sales"
                                                options={annualSalesRanges}
                                                value={formData.annualSales}
                                                onChange={(val) => setFormData(prev => ({ ...prev, annualSales: val }))}
                                                placeholder="Select sales range"
                                            />
                                        </div>
                                        <div>
                                            <label className="tw-block tw-text-xs tw-font-bold tw-uppercase tw-text-gray-500 tw-mb-2">Company Website</label>
                                            <input 
                                                required
                                                type="url"
                                                name="website"
                                                value={formData.website}
                                                onChange={handleFormChange}
                                                className="tw-w-full tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-xl tw-px-4 tw-py-3 focus:tw-border-[#00C5FB] tw-outline-none tw-transition-all"
                                                placeholder="https://protega.ai"
                                            />
                                        </div>
                                        <div className="md:tw-col-span-2">
                                            <label className="tw-block tw-text-xs tw-font-bold tw-uppercase tw-text-gray-500 tw-mb-2">How did you hear about us?</label>
                                            <input 
                                                name="hearAbout"
                                                value={formData.hearAbout}
                                                onChange={handleFormChange}
                                                className="tw-w-full tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-xl tw-px-4 tw-py-3 focus:tw-border-[#00C5FB] tw-outline-none tw-transition-all"
                                                placeholder="LinkedIn, Referral, etc."
                                            />
                                        </div>
                                        <button 
                                            type="submit"
                                            className="tw-w-full md:tw-col-span-2 tw-bg-[#00C5FB] tw-text-black tw-font-bold tw-py-4 tw-rounded-xl hover:tw-shadow-[0_0_20px_rgba(0,197,251,0.4)] tw-transition-all tw-flex tw-items-center tw-justify-center tw-gap-2"
                                        >
                                            Schedule Expert Session
                                            <Send className="tw-w-4 tw-h-4" />
                                        </button>
                                    </form>
                                </motion.div>
                            )}

                            {step === 'success' && (
                                <motion.div
                                    key="success-step"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="tw-relative tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-8 tw-py-20 lg:tw-py-28 tw-overflow-hidden"
                                >
                                    {/* Elevated 'Protega Milestone' Illustration */}
                                    <motion.div 
                                        initial={{ scale: 0.8, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ type: "spring", damping: 15, stiffness: 100 }}
                                        className="tw-relative tw-mb-16 tw-z-10"
                                    >
                                        {/* Background Glow */}
                                        <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-tr tw-from-[#00C5FB]/20 tw-to-blue-500/10 tw-blur-[60px] tw-rounded-full tw-animate-pulse" />
                                        
                                        <div className="tw-relative tw-w-48 tw-h-48 tw-flex tw-items-center tw-justify-center">
                                            {/* Architectural Concentric Rings */}
                                            <motion.svg 
                                                width="200" height="200" viewBox="0 0 200 200" 
                                                animate={{ rotate: 360 }}
                                                transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                                                className="tw-absolute"
                                            >
                                                <circle cx="100" cy="100" r="90" stroke="#00C5FB" strokeWidth="1" strokeDasharray="4 8" strokeOpacity="0.2" fill="none" />
                                                <circle cx="100" cy="100" r="70" stroke="#00C5FB" strokeWidth="0.5" strokeDasharray="2 4" strokeOpacity="0.3" fill="none" />
                                            </motion.svg>

                                            {/* The Milestone Pass (Internal Sphere) */}
                                            <motion.div 
                                                animate={{ y: [0, -8, 0] }}
                                                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                                                className="tw-relative tw-w-28 tw-h-28 tw-bg-gradient-to-br tw-from-white/10 tw-to-transparent tw-backdrop-blur-xl tw-border tw-border-white/20 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-shadow-2xl"
                                            >
                                                <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M15 30L25 40L45 20" stroke="#00C5FB" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round">
                                                        <motion.path 
                                                            initial={{ pathLength: 0 }}
                                                            animate={{ pathLength: 1 }}
                                                            transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
                                                            d="M15 30L25 40L45 20"
                                                        />
                                                    </path>
                                                </svg>
                                            </motion.div>

                                            {/* Organic Sparkles (Human Touch) */}
                                            {[...Array(5)].map((_, i) => (
                                                <motion.div
                                                    key={i}
                                                    initial={{ opacity: 0, scale: 0 }}
                                                    animate={{ 
                                                        opacity: [0, 1, 0],
                                                        scale: [0, 1, 0],
                                                        y: [0, -20, 0]
                                                    }}
                                                    transition={{ 
                                                        duration: 3, 
                                                        delay: i * 0.8, 
                                                        repeat: Infinity,
                                                        ease: "easeInOut"
                                                    }}
                                                    className="tw-absolute tw-w-1 tw-h-1 tw-bg-[#00C5FB] tw-rounded-full"
                                                    style={{ 
                                                        top: `${20 + Math.random() * 60}%`, 
                                                        left: `${20 + Math.random() * 60}%` 
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </motion.div>

                                    <motion.h2 
                                        initial={{ y: 20, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ delay: 0.2, type: "spring" }}
                                        className="tw-text-4xl md:tw-text-6xl tw-font-bold tw-text-white tw-mb-4 tw-tracking-tight tw-z-10"
                                    >
                                        See you soon, <span className="tw-text-[#00C5FB]">{formData.name.split(' ')[0] || 'Friend'}!</span>
                                    </motion.h2>
                                    <motion.p 
                                        initial={{ y: 20, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ delay: 0.3, type: "spring" }}
                                        className="tw-text-gray-400 tw-text-lg tw-mb-12 tw-max-w-sm tw-mx-auto tw-z-10"
                                    >
                                        {bookingPending
                                            ? "We've received your request for this slot. Our team will confirm it and send the calendar invite to your inbox shortly."
                                            : "Your expert walkthrough is confirmed. We've sent the calendar invite to your inbox."}
                                    </motion.p>

                                    {/* Premium Confirmation Ticket (The Digital Pass) */}
                                    <motion.div 
                                        initial={{ y: 40, opacity: 0, scale: 0.95 }}
                                        animate={{ y: 0, opacity: 1, scale: 1 }}
                                        transition={{ delay: 0.4, type: "spring", damping: 15 }}
                                        className="tw-relative tw-w-full tw-max-w-md tw-bg-white/[0.03] tw-backdrop-blur-2xl tw-border tw-border-white/10 tw-rounded-[2.5rem] tw-p-10 tw-mb-12 tw-overflow-hidden tw-shadow-2xl tw-z-10"
                                    >
                                        {/* Grainy Texture Overlay */}
                                        <div className="tw-absolute tw-inset-0 tw-opacity-[0.03] tw-pointer-events-none tw-bg-[url('https://grainy-gradients.vercel.app/noise.svg')] tw-bg-repeat" />
                                        
                                        {/* Mesh Gradient Accents */}
                                        <div className="tw-absolute tw-top-[-20%] tw-right-[-20%] tw-w-64 tw-h-64 tw-bg-[#00C5FB]/10 tw-blur-[80px] tw-rounded-full" />
                                        <div className="tw-absolute tw-bottom-[-20%] tw-left-[-20%] tw-w-64 tw-h-64 tw-bg-blue-500/5 tw-blur-[80px] tw-rounded-full" />
                                        
                                        <div className="tw-relative tw-flex tw-flex-col tw-gap-8">
                                            <div className="tw-flex tw-items-center tw-justify-between">
                                                <div className="tw-flex tw-flex-col tw-text-left">
                                                    <span className="tw-text-[10px] tw-uppercase tw-tracking-[0.2em] tw-text-gray-500 tw-font-black tw-mb-2">{bookingPending ? 'Requested Slot' : 'Confirmation Pass'}</span>
                                                    <span className="tw-text-2xl tw-font-bold tw-text-white">{format(selectedDate!, 'MMMM d, yyyy')}</span>
                                                </div>
                                                <div className="tw-w-14 tw-h-14 tw-bg-[#00C5FB]/10 tw-border tw-border-[#00C5FB]/20 tw-rounded-2xl tw-flex tw-items-center tw-justify-center tw-shadow-inner">
                                                    <Clock className="tw-w-7 tw-h-7 tw-text-[#00C5FB]" />
                                                </div>
                                            </div>

                                            <div className="tw-h-px tw-bg-white/5 tw-w-full" />

                                            <div className="tw-flex tw-flex-col tw-gap-1 tw-text-left">
                                                <span className="tw-text-[10px] tw-uppercase tw-tracking-[0.2em] tw-text-gray-500 tw-font-black">Scheduled Time</span>
                                                <div className="tw-flex tw-items-baseline tw-gap-3">
                                                    <span className="tw-text-4xl tw-font-black tw-text-[#00C5FB] tw-tabular-nums">{format(new Date(selectedTime!), 'hh:mm a')}</span>
                                                    <span className="tw-text-gray-600 tw-text-xs tw-font-bold">({Intl.DateTimeFormat().resolvedOptions().timeZone})</span>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>

                                    <motion.button 
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.8 }}
                                        onClick={() => navigate('/')}
                                        className="tw-group tw-relative tw-px-12 tw-py-4 tw-rounded-2xl tw-bg-white tw-text-black tw-font-black tw-text-lg tw-transition-all hover:tw-scale-105 active:tw-scale-95 tw-z-10"
                                    >
                                        Return Home
                                    </motion.button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                </div>

                {/* Footer Trust Section */}
                <motion.div 
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="tw-mt-32"
                >
                    <TrustedBy />
                </motion.div>
            </main>

            <Footer />
        </div>
    );
};

export default BookMeeting;
